import { useEffect, useState } from "react";
import { FilledButton, FlatButton, OutlinedButton } from "../../common/Button";
import Lightbox from "../../common/Lightbox";
import TextInputField from "../../common/TextInputField";
import useDisplayNotifications from "../../../hooks/useDisplayNotifications";
import { DisplayNotificationType } from "../../../models/DisplayNotification";
import DatePicker from "react-datepicker";
import { ITask } from "../../../data_models/task";
import { isTaskDefinitionOfDoneValid, isTaskDescriptionValid, isTaskEstimatedHoursValid, isTaskTitleValid, taskDefinitionOfDoneMinCharCount, taskDescriptionMinCharCount } from "../../../utils/taskUtils";

import InfoTooltip from "../../common/InfoTooltip";
import MultiTextSelect from "../../common/MultiTextSelect";

import RichTextEditor from "../../common/RichTextEditor";
import useTasks from "../../../hooks/useTasks";
import useArielle from "../../../hooks/useArielle";
import AISuggestion from "../../common/AISuggestion";
import RichText from "../../common/RichText";
import { IProject } from "../../../data_models/project";

const SESSION_STORAGE_KEY = 'create_task_form_data';

interface CreateNewTaskLightboxProps {
    projectId: string;
    onClose: () => void;
    onAddTask: (newTask: ITask) => void;
    onUpdateProject: (updatedProject: IProject) => void;
}

const CreateNewTaskLightbox: React.FC<CreateNewTaskLightboxProps> = ({ projectId, onClose, onAddTask, onUpdateProject }) => {

    const { createTask } = useTasks();
    const { lastReceivedTaskTitle,
        lastReceivedTaskDescription,
        lastReceivedTaskDefinitionOfDone,
        requestTaskTitle,
        requestTaskDescription,
        requestTaskDefinitionOfDone,
        setLastRequestedTaskTitleTimestamp,
        setLastRequestedTaskDescriptionTimestamp,
        setLastRequestedTaskDefinitionOfDoneTimestamp } = useArielle();

    const [initialized, setInitialized] = useState(false);
    const [loading, setLoading] = useState(false);
    const [createButtonActive, setCreateButtonActive] = useState(false);

    const [title, setTitle] = useState<string>();
    const [credits, setCredits] = useState<number>(20);
    const [description, setDescription] = useState<string>();
    const [definitionOfDone, setDefinitionOfDone] = useState<string>();
    const [dueDate, setDueDate] = useState<Date | null>();
    const [startDate, setStartDate] = useState<Date | null>();
    const [skills, setSkills] = useState<string[]>([]);
    const [tools, setTools] = useState<string[]>([]);

    const [suggestedTitle, setSuggestedTitle] = useState<string>();
    const [suggestedDescription, setSuggestedDescription] = useState<string>();
    const [suggestedDefinitionOfDone, setSuggestedDefinitionOfDone] = useState<string>();

    const [descriptionCharacters, setDescriptionCharacters] = useState(0);
    const [definitionOfDoneCharacters, setDefinitionOfDoneCharacters] = useState(0);

    const infoTitle = 'Select a meaningful title for the task. Avoid telling confidential information, as this text will be visible to talent to apply.';
    const infoStartDate = 'Optionally, specify a start date for this task. If not specified, the task will be considered as starting asap.';
    const infoDueDate = 'Optionally, specify a due date for this task. If not specified, the task will be considered as due asap.';
    const infoCredits = 'Specify the amount of credits a talent will earn for completing this task. Consider 1 credit worth 1 hour of work. Your client will have to approve this amount.';
    const infoDescription = 'This is a more detailed description of the task. It should contain all the information a talent needs to apply for this task. It is not public to everybody and can contain confidential information.';
    const infoDefinitionOfDone = 'Describe the criteria that the task must meet in order to be considered completed. This is used as an agreement between you, client and the talent. Once the talent meets this criteria, the task will be considered as completed and the talent will be paid for this task.';

    const infoSkills = 'List the skills that you expect the talent to have in order to complete the task.';
    const infoTools = 'List the tools that you expect the talent to use or know in order to complete the task.';

    const { addSuccessNotification, addErrorNotification } = useDisplayNotifications();

    const saveFormData = () => {
        const formData = {
            title,
            credits,
            description,
            definitionOfDone,
            dueDate: dueDate?.toISOString(),
            startDate: startDate?.toISOString(),
            skills,
            tools
        };
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(formData));
    };

    const loadFormData = () => {
        const savedData = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (savedData) {
            const formData = JSON.parse(savedData);
            setTitle(formData.title || '');
            setCredits(formData.credits || 0);
            setDescription(formData.description || '');
            setDefinitionOfDone(formData.definitionOfDone || '');
            setDueDate(formData.dueDate ? new Date(formData.dueDate) : null);
            setStartDate(formData.startDate ? new Date(formData.startDate) : null);
            setSkills(formData.skills || []);
            setTools(formData.tools || []);
        }
    };

    useEffect(() => {
        loadFormData();
        setInitialized(true);
    }, []);

    useEffect(() => {
        validateForm();
    }, [title,
        credits,
        description,
        definitionOfDone,
        dueDate,
        startDate,
        skills,
        tools,
    ]);

    useEffect(() => {
        if (initialized) {
            saveFormData();
        }
    }, [initialized, title, credits, description, definitionOfDone, dueDate, startDate, skills, tools]);

    const validateForm = () => {
        if (isTaskTitleValid(title || '')
            && isTaskEstimatedHoursValid(credits || 0)
            && isTaskDescriptionValid(description || '')
            && isTaskDefinitionOfDoneValid(definitionOfDone || '')) {
            setCreateButtonActive(true);
        } else {
            setCreateButtonActive(false);
        }
    }

    const onCreate = async () => {
        try {
            setLoading(true);

            const t: ITask = {
                projectId: projectId,
                title: title,
                estimatedCredits: credits || 0,
                description: description,
                definitionOfDone: definitionOfDone,
                dueDate: dueDate,
                startDate: startDate,
                requiredSkills: skills,
                requiredTools: tools,
            };

            console.log('t', t);

            const newTask = await createTask(projectId, t);
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
            onAddTask(newTask.task);
            onUpdateProject(newTask.project);
            addSuccessNotification(`Task "${newTask.task.title}" created successfully`);
            onClose();
        } catch (error: any) {
            addErrorNotification(error.message, 'Create Task Failed');
        } finally {
            setLoading(false);
        }
    };

    const onClear = () => {
        setTitle('');
        setCredits(0);
        setDescription('');
        setDefinitionOfDone('');
        setDueDate(undefined);
        setStartDate(undefined);
        setSkills([]);
        setTools([]);
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }

    useEffect(() => {
        if (initialized && title && title.split(' ').length > 1) {
            requestTaskTitle(projectId, { title });
        }
    }, [title]);

    useEffect(() => {
        if (initialized && (description?.length || 0) > 20) {
            requestTaskDescription(projectId, { description });
        }
    }, [description]);

    useEffect(() => {
        if (initialized && (definitionOfDone?.length || 0) > 20) {
            requestTaskDefinitionOfDone(projectId, { definitionOfDone });
        }
    }, [definitionOfDone]);

    useEffect(() => {
        if (lastReceivedTaskTitle) {
            setSuggestedTitle(lastReceivedTaskTitle.title);
        }
    }, [lastReceivedTaskTitle]);

    useEffect(() => {
        if (lastReceivedTaskDescription) {
            setSuggestedDescription(lastReceivedTaskDescription.description);
        }
    }, [lastReceivedTaskDescription]);

    useEffect(() => {
        if (lastReceivedTaskDefinitionOfDone) {
            setSuggestedDefinitionOfDone(lastReceivedTaskDefinitionOfDone.definitionOfDone);
        }
    }, [lastReceivedTaskDefinitionOfDone]);

    return (
        <Lightbox
            header='Create a Task'
            onConfirm={onCreate}
            loadingConfirm={loading}
            confirmText='Create'
            disabledConfirm={!createButtonActive}
            onCancel={onClose}
            onClose={onClose}
            onThirdAction={onClear}
            thirdActionText='Clear'
            content={
                <div className="flex flex-col items-start space-y-6" >
                    <div className="w-full">
                        <div className="flex items-center gap-2">
                            <label>Title</label>
                            <InfoTooltip text={infoTitle} />
                        </div>

                        <div className="w-full">
                            <TextInputField
                                value={title || ''}
                                onChange={(e) => {
                                    setTitle(e.target.value);
                                }}
                                placeholder="Title" />
                        </div>
                        {suggestedTitle && <div className="mt-2"><AISuggestion
                            onAccept={() => {
                                // suppress the AI suggestion from being called after accepting the suggestion
                                setLastRequestedTaskTitleTimestamp(Date.now());
                                setTitle(suggestedTitle)
                            }}
                        >{suggestedTitle}
                        </AISuggestion></div>}
                    </div>

                    <div className="w-full">
                        <div className="flex items-center gap-2">
                            <label>Start Date</label>
                            <InfoTooltip text={infoStartDate} />
                        </div>
                        <div className="">
                            <DatePicker
                                selected={startDate}
                                onChange={(date: Date | null) => setStartDate(date ? date : undefined)}
                                placeholderText="Start Date"
                                className="w-full"
                            />
                        </div>
                    </div>

                    <div className="w-full">
                        <div className="flex items-center gap-2">
                            <label>Due Date</label>
                            <InfoTooltip text={infoDueDate} />
                        </div>
                        <div className="">
                            <DatePicker
                                selected={dueDate}
                                onChange={(date: Date | null) => setDueDate(date ? date : undefined)}
                                placeholderText="Due Date"
                                className="w-full"
                            />
                        </div>
                    </div>

                    <div className="w-full">
                        <div className="flex items-center gap-2">
                            <label>Description</label>
                            <InfoTooltip text={infoDescription} />
                        </div>
                        <div className="w-full">
                            <RichTextEditor
                                value={description || ''}
                                onChange={(content) => { setDescription(content); }}
                                setCountCharacters={(count) => { setDescriptionCharacters(count); }} />
                        </div>
                        <div className="flex flex-row w-full justify-end">
                            <span className="text-xs text-text3 dark:text-dark-text3">{descriptionCharacters}</span>
                            <span className="ml-1 text-xs text-text3 dark:text-dark-text3">
                                (Min. {taskDescriptionMinCharCount} characters)
                            </span>
                        </div>
                        {suggestedDescription && <div className="mt-2"><AISuggestion
                            onAccept={() => {
                                // suppress the AI suggestion from being called after accepting the suggestion
                                setLastRequestedTaskDescriptionTimestamp(Date.now());
                                setDescription(suggestedDescription)
                            }}
                        >{<RichText text={suggestedDescription} />}
                        </AISuggestion></div>}
                    </div>

                    <div className="w-full">
                        <div className="flex items-center gap-2">
                            <label>Definition of Done</label>
                            <InfoTooltip text={infoDefinitionOfDone} />
                        </div>
                        <div className="w-full">
                            <RichTextEditor
                                value={definitionOfDone || ''}
                                onChange={(content) => { setDefinitionOfDone(content); }}
                                setCountCharacters={(count) => { setDefinitionOfDoneCharacters(count); }} />
                        </div>
                        <div className="flex flex-row w-full justify-end">
                            <span className="text-xs text-text3 dark:text-dark-text3">{definitionOfDoneCharacters}</span>
                            <span className="ml-1 text-xs text-text3 dark:text-dark-text3">
                                (Min. {taskDefinitionOfDoneMinCharCount} characters)
                            </span>
                        </div>
                        {suggestedDefinitionOfDone && <div className="mt-2"><AISuggestion
                            onAccept={() => {
                                // suppress the AI suggestion from being called after accepting the suggestion
                                setLastRequestedTaskDefinitionOfDoneTimestamp(Date.now());
                                setDefinitionOfDone(suggestedDefinitionOfDone)
                            }}
                        >{<RichText text={suggestedDefinitionOfDone} />}
                        </AISuggestion></div>}
                    </div>

                    <div className="w-full">
                        <div className="flex items-center gap-2">
                            <label>{`Credits`}</label>
                            <InfoTooltip text={infoCredits} />
                        </div>
                        <div className="w-full">
                            <TextInputField
                                value={credits?.toString() || '0'}
                                onChange={(e) => {
                                    setCredits(parseFloat(e.target.value));
                                }}
                                type='number'
                                placeholder="20" />
                        </div>
                    </div>

                    <div className="w-full">
                        <div className="flex items-center gap-2">
                            <label>Skills</label>
                            <InfoTooltip text={infoSkills} />
                        </div>

                        <MultiTextSelect
                            elements={skills}
                            setElements={setSkills}
                            placeholder="Skills" />

                    </div>

                    <div className="w-full">
                        <div className="flex items-center gap-2">
                            <label>Tools</label>
                            <InfoTooltip text={infoTools} />
                        </div>

                        <MultiTextSelect
                            elements={tools}
                            setElements={setTools}
                            placeholder="Tools" />
                    </div>
                </div>}
        />
    );
}

export default CreateNewTaskLightbox;
